import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import type {
  Partner,
  Program,
  ProgramRewardDuration,
  ProgramRewardUnit,
} from "../../shared/types";
import { money } from "../lib/format";
import { PARTNER_PORTAL } from "../../shared/config";
import { usePhone, useNative } from "../../shared/phone/media";
import { PnRow, Segmented } from "../../shared/phone/Row";
import { Sheet } from "../../shared/phone/Sheet";
import { CopyRow, FieldRow, linkParts } from "../views/partners/parts";
import { Icon } from "./ui/Icon";
import "../styles/add-partner.css";


type Tab = "email" | "link" | "qr";

/** The real join URL for this product's program (the portal, node P1). */
function joinUrl(productSlug: string | null): string {
  return productSlug ? `${PARTNER_PORTAL.url}/join/${productSlug}` : `${PARTNER_PORTAL.url}/join`;
}

/** Backend slug rule (workers/src/db/partners.ts): firstname + last initial. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "");
}
function previewSlug(first: string, last: string): string {
  return slugify(first) + slugify(last).charAt(0) || "partner";
}
function previewCoupon(first: string): string {
  return slugify(first).toUpperCase() || "PARTNER";
}

/** How long the reward pays, as the summary tail. */
function durationTail(duration: ProgramRewardDuration): string {
  if (duration === "first") return "of the first payment";
  if (duration === "recurring") return "of each payment, recurring";
  return "of every payment, for life";
}

/** Describe the program's default reward from the real program config. */
function programDefaultSummary(program: Program): string {
  if (program.rewardType === "ladder") {
    return "Tiered reward that grows as they refer more.";
  }
  const isPct = program.rewardUnit === "percent";
  const value = isPct ? `${program.rewardValueBpsOrCents / 100}%` : money(program.rewardValueBpsOrCents);
  return `${value} ${durationTail(program.rewardDuration)}.`;
}

/** Describe a custom per-partner reward from the modal's inputs. */
function customSummary(type: ProgramRewardUnit, value: number, duration: ProgramRewardDuration): string {
  const amount = type === "percent" ? `${value}%` : `$${value}`;
  return `This partner earns ${amount} ${durationTail(duration)}.`;
}

const RATE_PRESETS = [10, 30, 40] as const;

// ── copy-to-clipboard button ────────────────────────────────────────────────
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      // Clipboard blocked (insecure context / denied): leave the value on screen to copy by hand.
    }
    setCopied(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1100);
  };
  return (
    <button
      type="button"
      className={`ap-copy-btn${copied ? " copied" : ""}`}
      onClick={copy}
      aria-label={`Copy ${text}`}
    >
      {copied ? "Copied" : label}
    </button>
  );
}

// ── the modal ────────────────────────────────────────────────────────────────

/**
 * Add-partner modal (node B12): invite a real partner by email, or share the
 * real join link / QR. The email invite calls POST /products/:id/partners (S1),
 * which generates the slug + coupon and returns the partner; the confirmation
 * then shows that partner's real referral link and coupon.
 */
export function AddPartnerModal({
  productId,
  productName,
  productSlug,
  onClose,
  onInvited,
  onCopied,
}: {
  productId: string;
  productName: string;
  productSlug: string | null;
  onClose: () => void;
  onInvited: (partner: Partner) => void;
  /** On a phone or an iPad: says what a tap copied, in the screen's own note. */
  onCopied?: (label: string, ok: boolean) => void;
}) {
  const native = useNative();
  const onPhone = usePhone();
  const copied = onCopied ?? (() => {});
  const [tab, setTab] = useState<Tab>("email");
  // On a phone the reward is a page of its own inside the sheet.
  const [page, setPage] = useState<"form" | "reward">("form");
  const [program, setProgram] = useState<Program | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [rewardMode, setRewardMode] = useState<"default" | "custom">("default");
  const [customType, setCustomType] = useState<ProgramRewardUnit>("percent");
  const [customValue, setCustomValue] = useState("30");
  const [customDuration, setCustomDuration] = useState<ProgramRewardDuration>("first");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<Partner | null>(null);

  const closeRef = useRef<HTMLButtonElement>(null);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  const join = useMemo(() => joinUrl(productSlug), [productSlug]);

  // Fetch the real program: its default reward, whether coupons are on, and the
  // referral link base. Best-effort, the invite still works without it.
  useEffect(() => {
    let alive = true;
    api.partners
      .program(productId)
      .then((pd) => alive && setProgram(pd.program))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [productId]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Draw the real QR of the join URL whenever the QR tab is shown.
  useEffect(() => {
    if (tab !== "qr" || sent) return;
    const canvas = qrCanvasRef.current;
    if (canvas) drawQrToCanvas(canvas, join, { scale: 8, border: 4 });
  }, [tab, join, sent]);

  const linkBase = program?.linkBase ?? null;
  const couponEnabled = program?.couponEnabled ?? false;
  const rewardValue = Number.parseFloat(customValue);
  const rewardValid = Number.isFinite(rewardValue) && rewardValue > 0;

  const activePreset = RATE_PRESETS.find((p) => p === Number(customValue)) ?? null;

  // Live preview of the link + coupon this invite will generate (same slug/coupon
  // rules as the backend). The exact values are confirmed from the create response.
  const preview = useMemo(() => {
    const first = firstName.trim() || "partner";
    const last = lastName.trim();
    const slug = previewSlug(first, last);
    return {
      link: linkParts(linkBase, slug),
      coupon: previewCoupon(first),
    };
  }, [firstName, lastName, linkBase]);

  const submit = useCallback(async () => {
    if (submitting) return;
    if (!firstName.trim() || !lastName.trim()) {
      setErr("Add a first and last name.");
      return;
    }
    if (!email.trim()) {
      setErr("Add an email so they can join.");
      return;
    }
    if (rewardMode === "custom" && !rewardValid) {
      setErr("Set a reward greater than zero.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const { partner } = await api.partners.invite(productId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        rewardOverride:
          rewardMode === "custom"
            ? { type: customType, value: rewardValue, duration: customDuration }
            : null,
      });
      setSent(partner);
      onInvited(partner);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setSubmitting(false);
    }
  }, [
    submitting,
    firstName,
    lastName,
    email,
    phone,
    rewardMode,
    rewardValid,
    rewardValue,
    customType,
    customDuration,
    productId,
    onInvited,
  ]);

  const sentLink = sent ? linkParts(linkBase, sent.slug) : null;

  if (native) {
    const shownJoin = join.replace(/^https?:\/\//, "");
    const customLabel = rewardValid ? `${customType === "percent" ? `${rewardValue}%` : `$${rewardValue}`}, custom` : "Custom";
    const cancel = (
      <button type="button" className="pn-tbtn" onClick={onClose} disabled={submitting}>
        Cancel
      </button>
    );
    const done = (
      <button type="button" className="pn-tbtn strong" onClick={onClose}>
        Done
      </button>
    );

    if (sent && sentLink) {
      return (
        <Sheet open onClose={onClose} title="Invite sent" right={done} tall={onPhone} className="pa-form-sheet">
          <div className="pa-sent">
            <span className="pa-sent-ic">
              <Icon name="check" />
            </span>
            <b>Invite sent</b>
            <p>
              {sent.firstName} gets an email to join {productName}. Their link is live now.
            </p>
          </div>
          <div className="pn-grp">
            <CopyRow big icon="link" label="Their link" value={sentLink.full} shown={sentLink.shown} onCopied={copied} />
            {sent.couponCode ? <CopyRow big icon="coin" label="Their coupon code" value={sent.couponCode} shown={sent.couponCode} onCopied={copied} /> : null}
          </div>
        </Sheet>
      );
    }

    if (page === "reward") {
      return (
        <Sheet
          open
          onClose={onClose}
          title="Reward"
          tall={onPhone}
          className="pa-form-sheet"
          left={
            <button type="button" className="pn-tbtn" onClick={() => setPage("form")}>
              Back
            </button>
          }
          foot={
            <button type="button" className="pn-btn pri wide" onClick={() => setPage("form")}>
              Done
            </button>
          }
        >
          <div className="pn-grp">
            <button type="button" className="pn-row" onClick={() => setRewardMode("default")} aria-pressed={rewardMode === "default"}>
              <span className="m">
                <span className="t">Program default</span>
                <span className="s">{program ? programDefaultSummary(program) : "Loading the reward…"}</span>
              </span>
              {rewardMode === "default" ? (
                <span className="pa-tick">
                  <Icon name="check" />
                </span>
              ) : null}
            </button>
            <button type="button" className="pn-row" onClick={() => setRewardMode("custom")} aria-pressed={rewardMode === "custom"}>
              <span className="m">
                <span className="t">Custom for this partner</span>
                <span className="s">A different reward for this one person</span>
              </span>
              {rewardMode === "custom" ? (
                <span className="pa-tick">
                  <Icon name="check" />
                </span>
              ) : null}
            </button>
          </div>
          {rewardMode === "custom" ? (
            <>
              <div className="pn-cap pa-capgap">Their reward</div>
              <Segmented<ProgramRewardUnit>
                label="Reward type"
                value={customType}
                onChange={setCustomType}
                options={[
                  { value: "percent", label: "Percent" },
                  { value: "fixed", label: "Flat $" },
                ]}
              />
              <div className="pn-grp pa-gap">
                <FieldRow
                  id="pa-add-rate"
                  label={customType === "percent" ? "Rate" : "Per sale"}
                  value={customValue}
                  onChange={setCustomValue}
                  suf={customType === "percent" ? "%" : undefined}
                  pre={customType === "percent" ? undefined : "$"}
                />
              </div>
              <div className="pn-cap pa-capgap">Pay for how long</div>
              <Segmented<ProgramRewardDuration>
                label="Pay for how long"
                value={customDuration}
                onChange={setCustomDuration}
                options={[
                  { value: "first", label: "First payment" },
                  { value: "recurring", label: "Recurring" },
                  { value: "forever", label: "Forever" },
                ]}
              />
              <p className="pn-note">{rewardValid ? customSummary(customType, rewardValue, customDuration) : "Set a reward greater than zero."}</p>
            </>
          ) : null}
        </Sheet>
      );
    }

    return (
      <Sheet
        open
        onClose={onClose}
        title="Add partners"
        tall={onPhone}
        className="pa-form-sheet"
        left={tab === "email" ? cancel : undefined}
        right={tab === "email" ? undefined : done}
        foot={
          tab === "email" ? (
            <button type="button" className="pn-btn pri wide" onClick={submit} disabled={submitting}>
              {submitting ? "Sending…" : "Send invite"}
            </button>
          ) : tab === "qr" ? (
            <button type="button" className="pn-btn soft wide" onClick={() => downloadQr(qrCanvasRef.current)}>
              <Icon name="down" />
              Download
            </button>
          ) : undefined
        }
      >
        <div className="pa-sheet-seg">
          <Segmented<Tab>
            label="How to add partners"
            value={tab}
            onChange={setTab}
            options={[
              { value: "email", label: "Email" },
              { value: "link", label: "Link" },
              { value: "qr", label: "QR" },
            ]}
          />
        </div>
        {tab === "email" ? (
          <>
            <form
              className="pn-grp"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="pn-irow">
                <label htmlFor="pa-add-first">First name</label>
                <input id="pa-add-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
              </div>
              <div className="pn-irow">
                <label htmlFor="pa-add-last">Last name</label>
                <input id="pa-add-last" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
              </div>
              <div className="pn-irow">
                <label htmlFor="pa-add-email">Email</label>
                <input
                  id="pa-add-email"
                  type="email"
                  inputMode="email"
                  autoCapitalize="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@email.com"
                  autoComplete="email"
                />
              </div>
              <div className="pn-irow">
                <label htmlFor="pa-add-phone">Phone</label>
                <input id="pa-add-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" autoComplete="tel" />
              </div>
              <button type="submit" hidden />
            </form>
            <div className="pn-grp pa-gap">
              <PnRow
                icon={<Icon name="coin" />}
                title="Reward"
                value={rewardMode === "custom" ? customLabel : "Program default"}
                onClick={() => setPage("reward")}
                chevron
              />
            </div>
            {err ? (
              <p className="pa-err" role="alert">
                {err}
              </p>
            ) : null}
            <p className="pn-note">Their link{couponEnabled ? " and coupon code are" : " is"} made when you send.</p>
          </>
        ) : tab === "link" ? (
          <>
            <div className="pn-grp">
              <CopyRow big icon="link" label="Join link" value={join} shown={shownJoin} onCopied={copied} />
            </div>
            <p className="pn-note">Post this link anywhere. Whoever opens it signs up with their own details, then signs the agreement.</p>
          </>
        ) : (
          <>
            <div className="pa-qr">
              <canvas ref={qrCanvasRef} aria-label={`QR code for ${join}`} role="img" />
            </div>
            <p className="pn-note pa-c">Print or share this code. It opens the join link.</p>
          </>
        )}
      </Sheet>
    );
  }

  return (
    <div
      className="add-partner-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ap-modal" role="dialog" aria-modal="true" aria-label="Add a partner">
        {sent && sentLink ? (
          <>
            <div className="ap-head">
              <h3>Invite sent</h3>
              <button type="button" className="ap-x" onClick={onClose} ref={closeRef} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="ap-body">
              <div className="ap-sent">
                <div className="ap-chk" aria-hidden="true">
                  ✓
                </div>
                <h4>Invite sent</h4>
                <p>
                  {sent.firstName} gets an email to join {productName}. Their link is live now.
                </p>
                <div className="ap-sent-links">
                  <div className="ap-sent-row">
                    <span className="ap-chip" title={sentLink.full}>
                      {sentLink.shown}
                    </span>
                    <CopyButton text={sentLink.full} />
                  </div>
                  {sent.couponCode && (
                    <div className="ap-sent-row">
                      <span className="ap-chip coupon">{sent.couponCode}</span>
                      <CopyButton text={sent.couponCode} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="ap-foot">
              <button type="button" className="ap-btn" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="ap-head">
              <h3>Add a partner</h3>
              <button type="button" className="ap-x" onClick={onClose} ref={closeRef} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="ap-tabs" role="tablist" aria-label="How to add a partner">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "email"}
                className={tab === "email" ? "on" : ""}
                onClick={() => setTab("email")}
              >
                Invite by email
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "link"}
                className={tab === "link" ? "on" : ""}
                onClick={() => setTab("link")}
              >
                Share link
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "qr"}
                className={tab === "qr" ? "on" : ""}
                onClick={() => setTab("qr")}
              >
                QR code
              </button>
            </div>

            <div className="ap-body">
              {tab === "email" && (
                <div className="ap-pane">
                  <div className="ap-frow2">
                    <div className="ap-field">
                      <label htmlFor="ap-first">First name</label>
                      <input
                        id="ap-first"
                        className="ap-inp"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="ap-field">
                      <label htmlFor="ap-last">Last name</label>
                      <input
                        id="ap-last"
                        className="ap-inp"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                  <div className="ap-field">
                    <label htmlFor="ap-email">Email</label>
                    <input
                      id="ap-email"
                      className="ap-inp"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@email.com"
                      autoComplete="email"
                    />
                  </div>
                  <div className="ap-field">
                    <label htmlFor="ap-phone">Phone</label>
                    <input
                      id="ap-phone"
                      className="ap-inp"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 555 000 0000"
                      autoComplete="tel"
                    />
                  </div>

                  <div className="ap-field">
                    <span className="ap-flabel">Reward</span>
                    <div className="ap-seg" role="group" aria-label="Reward">
                      <button
                        type="button"
                        className={rewardMode === "default" ? "on" : ""}
                        onClick={() => setRewardMode("default")}
                      >
                        Program default
                      </button>
                      <button
                        type="button"
                        className={rewardMode === "custom" ? "on" : ""}
                        onClick={() => setRewardMode("custom")}
                      >
                        Custom for this partner
                      </button>
                    </div>

                    {rewardMode === "default" ? (
                      <div className="ap-reward-default">
                        <div className="ap-summary">
                          {program ? programDefaultSummary(program) : "Loading the reward…"}
                        </div>
                      </div>
                    ) : (
                      <div className="ap-reward-custom">
                        <div>
                          <span className="ap-flabel">Type</span>
                          <div className="ap-seg" role="group" aria-label="Reward type">
                            <button
                              type="button"
                              className={customType === "percent" ? "on" : ""}
                              onClick={() => setCustomType("percent")}
                            >
                              Percent
                            </button>
                            <button
                              type="button"
                              className={customType === "fixed" ? "on" : ""}
                              onClick={() => setCustomType("fixed")}
                            >
                              Flat
                            </button>
                          </div>
                        </div>
                        <div className="ap-presets">
                          {RATE_PRESETS.map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`ap-preset${activePreset === p ? " on" : ""}`}
                              onClick={() => setCustomValue(String(p))}
                            >
                              {p}
                            </button>
                          ))}
                          <label className={`ap-preset custom${activePreset === null ? " on" : ""}`}>
                            Custom
                            <input
                              inputMode="decimal"
                              value={customValue}
                              onChange={(e) => setCustomValue(e.target.value)}
                              aria-label="Custom reward value"
                            />
                          </label>
                        </div>
                        <div>
                          <span className="ap-flabel">Pay for how long</span>
                          <div className="ap-seg" role="group" aria-label="Reward duration">
                            <button
                              type="button"
                              className={customDuration === "first" ? "on" : ""}
                              onClick={() => setCustomDuration("first")}
                            >
                              First payment
                            </button>
                            <button
                              type="button"
                              className={customDuration === "recurring" ? "on" : ""}
                              onClick={() => setCustomDuration("recurring")}
                            >
                              Recurring
                            </button>
                            <button
                              type="button"
                              className={customDuration === "forever" ? "on" : ""}
                              onClick={() => setCustomDuration("forever")}
                            >
                              Forever
                            </button>
                          </div>
                        </div>
                        <div className="ap-summary">
                          {rewardValid
                            ? customSummary(customType, rewardValue, customDuration)
                            : "Set a reward greater than zero."}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={couponEnabled ? "ap-frow2" : ""}>
                    <div className="ap-field">
                      <span className="ap-flabel">Their link</span>
                      <div className="ap-chip" title={preview.link.full}>
                        {preview.link.shown}
                      </div>
                    </div>
                    {couponEnabled && (
                      <div className="ap-field">
                        <span className="ap-flabel">Coupon code</span>
                        <div className="ap-chip coupon">{preview.coupon}</div>
                      </div>
                    )}
                  </div>
                  <p className="ap-hint">Generated when you send. Confirmed on the next screen.</p>

                  {err && <div className="ap-err">{err}</div>}
                </div>
              )}

              {tab === "link" && (
                <div className="ap-pane">
                  <p className="ap-note">
                    Post this link anywhere. Whoever opens it signs up with their own details, then
                    signs the agreement.
                  </p>
                  <div className="ap-copyrow">
                    <input value={join} readOnly aria-label="Join link" />
                    <CopyButton text={join} />
                  </div>
                </div>
              )}

              {tab === "qr" && (
                <div className="ap-pane ap-qr-pane">
                  <p className="ap-note">Print or share this code. It opens the join link.</p>
                  <div className="ap-qrbox">
                    <canvas ref={qrCanvasRef} aria-label={`QR code for ${join}`} role="img" />
                  </div>
                  <div className="ap-qr-actions">
                    <button
                      type="button"
                      className="ap-btn ghost sm"
                      onClick={() => downloadQr(qrCanvasRef.current)}
                    >
                      Download QR
                    </button>
                  </div>
                </div>
              )}
            </div>

            {tab === "email" ? (
              <div className="ap-foot">
                <button type="button" className="ap-btn ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button type="button" className="ap-btn" onClick={submit} disabled={submitting}>
                  {submitting ? "Sending…" : "Send invite"}
                </button>
              </div>
            ) : (
              <div className="ap-foot">
                <button type="button" className="ap-btn" onClick={onClose}>
                  Done
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── PNG download of the rendered QR ──────────────────────────────────────────
function downloadQr(canvas: HTMLCanvasElement | null): void {
  if (!canvas) return;
  const a = document.createElement("a");
  a.download = "flashadz-partner-join-qr.png";
  a.href = canvas.toDataURL("image/png");
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ─────────────────────────────────────────────────────────────────────────────
// Real QR Code generator (byte mode), self-contained, no dependency, no network.
// Implements the QR Code Model 2 spec: Reed-Solomon ECC, function patterns,
// data masking with penalty-based mask selection, and format/version info.
// Algorithm mirrors Nayuki's public-domain QR Code generator, trimmed to the
// single byte segment a URL needs. Produces a genuinely scannable matrix.
// ─────────────────────────────────────────────────────────────────────────────

// Error-correction level -> {ordinal (table index), formatBits}. Level M gives a
// good density/robustness balance for a printed/shared code.
const ECC = { L: { ord: 0, fmt: 1 }, M: { ord: 1, fmt: 0 }, Q: { ord: 2, fmt: 3 }, H: { ord: 3, fmt: 2 } } as const;
type EccKey = keyof typeof ECC;

// [level.ord][version] -> ECC codewords per block. Version 0 is unused (-1).
const ECC_CODEWORDS_PER_BLOCK: readonly (readonly number[])[] = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];

// [level.ord][version] -> number of ECC blocks.
const NUM_ECC_BLOCKS: readonly (readonly number[])[] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

function getBit(x: number, i: number): boolean {
  return ((x >>> i) & 1) !== 0;
}

function getNumRawDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
}

function getNumDataCodewords(ver: number, eccOrd: number): number {
  return (
    Math.floor(getNumRawDataModules(ver) / 8) -
    ECC_CODEWORDS_PER_BLOCK[eccOrd][ver] * NUM_ECC_BLOCKS[eccOrd][ver]
  );
}

function reedSolomonMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function reedSolomonComputeDivisor(degree: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < degree - 1; i++) result.push(0);
  result.push(1);
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = reedSolomonMultiply(result[j], root);
      if (j + 1 < result.length) result[j] ^= result[j + 1];
    }
    root = reedSolomonMultiply(root, 0x02);
  }
  return result;
}

function reedSolomonComputeRemainder(data: number[], divisor: number[]): number[] {
  const result = divisor.map(() => 0);
  for (const b of data) {
    const factor = b ^ (result.shift() as number);
    result.push(0);
    for (let i = 0; i < divisor.length; i++) result[i] ^= reedSolomonMultiply(divisor[i], factor);
  }
  return result;
}

/** A finished QR symbol: the module grid plus its size. */
interface QrMatrix {
  size: number;
  modules: boolean[][];
}

function getAlignmentPatternPositions(ver: number, size: number): number[] {
  if (ver === 1) return [];
  const numAlign = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

function buildQrMatrix(ver: number, ecc: (typeof ECC)[EccKey], dataCodewords: number[]): QrMatrix {
  const size = ver * 4 + 17;
  const modules: boolean[][] = Array.from({ length: size }, () => Array.from({ length: size }, () => false));
  const isFunction: boolean[][] = Array.from({ length: size }, () => Array.from({ length: size }, () => false));

  const setFn = (x: number, y: number, dark: boolean): void => {
    modules[y][x] = dark;
    isFunction[y][x] = true;
  };

  const drawFinder = (x: number, y: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < size && yy >= 0 && yy < size) setFn(xx, yy, dist !== 2 && dist !== 4);
      }
    }
  };

  const drawAlign = (x: number, y: number): void => {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) setFn(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  };

  const drawFormatBits = (mask: number): void => {
    const data = (ecc.fmt << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;
    for (let i = 0; i <= 5; i++) setFn(8, i, getBit(bits, i));
    setFn(8, 7, getBit(bits, 6));
    setFn(8, 8, getBit(bits, 7));
    setFn(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i++) setFn(14 - i, 8, getBit(bits, i));
    for (let i = 0; i < 8; i++) setFn(size - 1 - i, 8, getBit(bits, i));
    for (let i = 8; i < 15; i++) setFn(8, size - 15 + i, getBit(bits, i));
    setFn(8, size - 8, true);
  };

  const drawVersion = (): void => {
    if (ver < 7) return;
    let rem = ver;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (ver << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const color = getBit(bits, i);
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      setFn(a, b, color);
      setFn(b, a, color);
    }
  };

  // Function patterns.
  for (let i = 0; i < size; i++) {
    setFn(6, i, i % 2 === 0);
    setFn(i, 6, i % 2 === 0);
  }
  drawFinder(3, 3);
  drawFinder(size - 4, 3);
  drawFinder(3, size - 4);
  const alignPos = getAlignmentPatternPositions(ver, size);
  const numAlign = alignPos.length;
  for (let i = 0; i < numAlign; i++) {
    for (let j = 0; j < numAlign; j++) {
      if (!((i === 0 && j === 0) || (i === 0 && j === numAlign - 1) || (i === numAlign - 1 && j === 0))) {
        drawAlign(alignPos[i], alignPos[j]);
      }
    }
  }
  drawFormatBits(0);
  drawVersion();

  // Interleave data + ECC across blocks, then lay the codewords into the grid.
  const allCodewords = addEccAndInterleave(ver, ecc.ord, dataCodewords);
  let idx = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    const col = right === 6 ? 5 : right;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = col - j;
        const upward = ((col + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!isFunction[y][x] && idx < allCodewords.length * 8) {
          modules[y][x] = getBit(allCodewords[idx >>> 3], 7 - (idx & 7));
          idx++;
        }
      }
    }
  }

  // Pick the mask with the lowest penalty.
  let bestMask = 0;
  let minPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, size, mask);
    drawFormatBits(mask);
    const penalty = getPenaltyScore(modules, size);
    if (penalty < minPenalty) {
      minPenalty = penalty;
      bestMask = mask;
    }
    applyMask(modules, isFunction, size, mask); // undo
  }
  applyMask(modules, isFunction, size, bestMask);
  drawFormatBits(bestMask);

  return { size, modules };
}

function addEccAndInterleave(ver: number, eccOrd: number, data: number[]): number[] {
  const numBlocks = NUM_ECC_BLOCKS[eccOrd][ver];
  const blockEccLen = ECC_CODEWORDS_PER_BLOCK[eccOrd][ver];
  const rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
  const numShort = numBlocks - (rawCodewords % numBlocks);
  const shortLen = Math.floor(rawCodewords / numBlocks);
  const blocks: number[][] = [];
  const rsDiv = reedSolomonComputeDivisor(blockEccLen);
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dat = data.slice(k, k + shortLen - blockEccLen + (i < numShort ? 0 : 1));
    k += dat.length;
    const ecc = reedSolomonComputeRemainder(dat, rsDiv);
    if (i < numShort) dat.push(0);
    blocks.push(dat.concat(ecc));
  }
  const result: number[] = [];
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      if (i !== shortLen - blockEccLen || j >= numShort) result.push(blocks[j][i]);
    }
  }
  return result;
}

function applyMask(modules: boolean[][], isFunction: boolean[][], size: number, mask: number): void {
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (isFunction[y][x]) continue;
      let invert = false;
      switch (mask) {
        case 0:
          invert = (x + y) % 2 === 0;
          break;
        case 1:
          invert = y % 2 === 0;
          break;
        case 2:
          invert = x % 3 === 0;
          break;
        case 3:
          invert = (x + y) % 3 === 0;
          break;
        case 4:
          invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
          break;
        case 5:
          invert = ((x * y) % 2) + ((x * y) % 3) === 0;
          break;
        case 6:
          invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
          break;
        default:
          invert = ((((x + y) % 2) + ((x * y) % 3)) % 2) === 0;
          break;
      }
      if (invert) modules[y][x] = !modules[y][x];
    }
  }
}

function finderPenaltyCountPatterns(runHistory: number[]): number {
  const n = runHistory[1];
  const core = n > 0 && runHistory[2] === n && runHistory[3] === n * 3 && runHistory[4] === n && runHistory[5] === n;
  return (
    (core && runHistory[0] >= n * 4 && runHistory[6] >= n ? 1 : 0) +
    (core && runHistory[6] >= n * 4 && runHistory[0] >= n ? 1 : 0)
  );
}

function finderPenaltyAddHistory(run: number, runHistory: number[], size: number): void {
  if (runHistory[0] === 0) run += size;
  runHistory.pop();
  runHistory.unshift(run);
}

function finderPenaltyTerminate(color: boolean, run: number, runHistory: number[], size: number): number {
  if (color) {
    finderPenaltyAddHistory(run, runHistory, size);
    run = 0;
  }
  run += size;
  finderPenaltyAddHistory(run, runHistory, size);
  return finderPenaltyCountPatterns(runHistory);
}

function getPenaltyScore(modules: boolean[][], size: number): number {
  let result = 0;

  for (let y = 0; y < size; y++) {
    let color = false;
    let run = 0;
    const hist = [0, 0, 0, 0, 0, 0, 0];
    for (let x = 0; x < size; x++) {
      if (modules[y][x] === color) {
        run++;
        if (run === 5) result += PENALTY_N1;
        else if (run > 5) result++;
      } else {
        finderPenaltyAddHistory(run, hist, size);
        if (!color) result += finderPenaltyCountPatterns(hist) * PENALTY_N3;
        color = modules[y][x];
        run = 1;
      }
    }
    result += finderPenaltyTerminate(color, run, hist, size) * PENALTY_N3;
  }

  for (let x = 0; x < size; x++) {
    let color = false;
    let run = 0;
    const hist = [0, 0, 0, 0, 0, 0, 0];
    for (let y = 0; y < size; y++) {
      if (modules[y][x] === color) {
        run++;
        if (run === 5) result += PENALTY_N1;
        else if (run > 5) result++;
      } else {
        finderPenaltyAddHistory(run, hist, size);
        if (!color) result += finderPenaltyCountPatterns(hist) * PENALTY_N3;
        color = modules[y][x];
        run = 1;
      }
    }
    result += finderPenaltyTerminate(color, run, hist, size) * PENALTY_N3;
  }

  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = modules[y][x];
      if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) {
        result += PENALTY_N2;
      }
    }
  }

  let dark = 0;
  for (const row of modules) for (const cell of row) if (cell) dark++;
  const total = size * size;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  result += k * PENALTY_N4;
  return result;
}

function appendBits(val: number, len: number, bb: number[]): void {
  for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1);
}

/** Encode text (byte mode) into a QR matrix at the smallest fitting version. */
function encodeQr(text: string, level: EccKey = "M"): QrMatrix {
  const ecc = ECC[level];
  const data = Array.from(new TextEncoder().encode(text));

  let version = 0;
  let capacityBits = 0;
  for (let v = 1; v <= 40; v++) {
    const cap = getNumDataCodewords(v, ecc.ord) * 8;
    const ccBits = v <= 9 ? 8 : 16;
    if (4 + ccBits + data.length * 8 <= cap) {
      version = v;
      capacityBits = cap;
      break;
    }
  }
  if (version === 0) throw new Error("QR data too long");

  const bb: number[] = [];
  appendBits(0x4, 4, bb); // byte mode indicator
  appendBits(data.length, version <= 9 ? 8 : 16, bb);
  for (const b of data) appendBits(b, 8, bb);
  appendBits(0, Math.min(4, capacityBits - bb.length), bb); // terminator
  appendBits(0, (8 - (bb.length % 8)) % 8, bb); // byte-align
  for (let pad = 0xec; bb.length < capacityBits; pad ^= 0xec ^ 0x11) appendBits(pad, 8, bb);

  const codewords: number[] = [];
  for (let i = 0; i < bb.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bb[i + j];
    codewords.push(byte);
  }
  return buildQrMatrix(version, ecc, codewords);
}

/** Draw a real, scannable QR of `text` onto a canvas (dark-on-white, downloadable). */
function drawQrToCanvas(
  canvas: HTMLCanvasElement,
  text: string,
  opts: { scale?: number; border?: number; dark?: string; light?: string } = {},
): void {
  const qr = encodeQr(text, "M");
  const scale = opts.scale ?? 8;
  const border = opts.border ?? 4;
  const dim = (qr.size + border * 2) * scale;
  canvas.width = dim;
  canvas.height = dim;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.fillStyle = opts.light ?? "#ffffff";
  ctx.fillRect(0, 0, dim, dim);
  ctx.fillStyle = opts.dark ?? "#0b0e13";
  for (let y = 0; y < qr.size; y++) {
    for (let x = 0; x < qr.size; x++) {
      if (qr.modules[y][x]) ctx.fillRect((x + border) * scale, (y + border) * scale, scale, scale);
    }
  }
}
